/* global Paho */
import queryString from 'query-string';
import uuid from 'uuid';
import apiService from './api/ApiService';
import assessmentActions from '../actions/AssessmentActions';
import sessionActions from '../actions/SessionActions';
import settingsActions from '../actions/SettingsActions';
import { setIdVerificationStatus } from '../actions/IdVerificationActions';
import storeManager from '../store/StoreManager';
import globalsService from './GlobalsService';
import navigationService from './NavigationService';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import stateCacheService from './StateCache';
import itemFlagActions from '../actions/ItemFlagActions';
import backgroundAudioService from './BackgroundAudioService';
import routeTypes from '../constants/RouteTypes';
import parentTestService from './ParentTestService';
import timeService from './TimeService';
import messagesService from './MessagesService';
import localCache from './LocalCache';
import stateRestorationService from './StateRestoration';
import catDeliveryService from './CatDeliveryService';
import remoteOralExamService from './RemoteOralExamService';
import remoteOralExamActions from '../actions/RemoteOralExamActions';
import liveSessionMessageHandler from './AWSIOTMessageHandler/LiveSessionMessageHandler';
import pauseStateHandler from './AWSIOTMessageHandler/PauseStateHandler';
import environmentNames from '../environmentNames';
import utilService from './UtilService';

require('paho-mqtt');

const topicCallbackMap = {};
const RECONNECT_INTERVAL = 20 * 1000;

function updateSetting(setting) {
    const store = storeManager.getStore();
    if ('numbering_scheme' in setting) {
        // if the question numbering changed we need to reload the question set
        handleNumberingScheme(store);
    }

    if ('enable_flagging' in setting) {
        updateFlagging(store, setting.enable_flagging);
    }

    store.dispatch(
        settingsActions.updateSetting(setting),
    );

    if (navigationService.isOnTOCPage() && !settingsReader.showTableOfContents()) {
        navigationService.navigateToFirstQuestion();
    }
}

function handleNumberingScheme(store) {
    apiService
        .fetchQuestionSet()
        .then((assessment) => {
            store.dispatch(assessmentActions.assessmentLoadSuccess(assessment));
        });
}


async function handleCatQsReady(catRevisionId) {
    if (catRevisionId) {
        catDeliveryService.handleUpdatedCatQS(catRevisionId);
    }
}

function updateFlagging(store, enabled) {
    if (enabled) {
        return;
    }

    stateCacheService
        .getStateCacheInstance()
        .setFlaggedItems([]);

    store.dispatch(
        itemFlagActions.setFlaggedItems([]),
    );
}

function handleRewindAudio(body) {
    if (!body) {
        return;
    }

    window.console.log('got message for rewind ', body);
    if (location.hash.includes(routeTypes.ROUTE_TYPE_OPTIONS)) {
        navigationService.exitFromOptions();
    }

    if (body.REWIND_AUDIO_MINUTES) {
        backgroundAudioService.rewindByMinutes(body.REWIND_AUDIO_MINUTES);
    } else if (body.REWIND_BY_SECTION) {
        backgroundAudioService.rewindBySection(body.REWIND_BY_SECTION, body.EXTRA_TIME);
    }
}

function handleStartTest(body) {
    parentTestService.startChildTestAndNavigate(body.assessmentRunId);
}

function handleIEPFileUpload(body) {
    if (!body) {
        return;
    }
    window.console.log('got message of IEP file upload to reload response ', body);
    try {
        stateRestorationService.loadSavedResponsesForExam();
    } catch (error) {
        window.console.error(error);
    }
}

function handleMessage() {
    messagesService.fetchCompareAndAlertMessages();
}

function handleExtraTime(body) {
    if (!body) {
        return;
    }
    timeService.fetchTimeRemaining(true, true);
}

const handleRemoteOralExamSessionUpdate = () => {
    remoteOralExamService.fetchAndProcessSessionInfo();
};

const handleWaitingRoomStatusUpdate = (body) => {
    if (!body) {
        return;
    }
    const store = storeManager.getStore();
    store.dispatch(remoteOralExamActions.setWaitingRoomStatus(body));
};

const handleIdVerificationUpdate = (body) => {
    if (!body) {
        return;
    }
    const store = storeManager.getStore();
    store.dispatch(
        setIdVerificationStatus(body.idVerificationStatus),
    );
};

const onCandidateOffline = () => window.console.log('We are offline!');

const onCandidateBackOnline = () => {
    window.console.log('We are back online, resurrecting push channel!');
    setTimeout(() => {
        AWSIOTService.fetchUrlAndConnect(true);
        // we keep the reconnectTimerId as a failsafe mechanism.
        if (AWSIOTService.reconnectTimerId) {
            clearTimeout(AWSIOTService.reconnectTimerId);
        }
        // we need to wait for some time because chrome detects online event faster than fetch api can use the network
    }, 3000);
};

const AWSIOTService = {
    reconnectTimerId: null,
    allowIOTConnection() {
        // only use websockets when previewing question sets or taking exam
        return localCache.isLocalStorageAvailable()
            && (sessionReader.isContextExam() || sessionReader.isContextPreview())
            && !sessionReader.isPublicExam()
            && !this.isIOTConnectionDisabled();
    },

    isIOTConnectionDisabled() {
        return process.env.NODE_ENV !== environmentNames.PRODUCTION
            && utilService.getUrlParam('DISABLE_PUSH_PUBLISHER_FOR_TT') === 'true';
    },

    loadSignedWebSocketUrl(useRetry = true) {
        const store = storeManager.getStore();

        return apiService.fetchSignedWebSocketUrl(useRetry)
            .then((result) => {
                store.dispatch(
                    sessionActions.setSignedWebSocketUrl(result.signedWebSocketUrl),
                );
            });
    },

    reconnectClient() {
        if (!this.reconnectTimerId) {
            this.reconnectTimerId = setTimeout(this.fetchUrlAndConnect.bind(this, true), RECONNECT_INTERVAL);
        }
    },

    /**
     * @param isReconnect Whether or not this is part of a reconnection attempt.
     */
    fetchUrlAndConnect(isReconnect) {
        window.console.info('Reconnecting to AWS IoT');
        this.loadSignedWebSocketUrl(false)
            .then(() => {
                this.reconnectTimerId = null;
                this.setupClient(isReconnect);
            })
            .catch((error) => {
                window.console.warn('Failed reconnecting to AWS IoT', error);
                this.reconnectTimerId = null;
                this.reconnectClient();
            });
    },

    onConnectionLost(e) {
        window.console.info('Client disconnected from AWS IoT', e);
        this.reconnectClient();
    },

    onMessageArrived(message) {
        const messageObject = JSON.parse(message.payloadString);
        const callback = topicCallbackMap[message.destinationName];
        if (typeof callback === 'function') {
            callback(messageObject.data);
        }
    },

    subscribeTopic(topic, callback, isChildTopic = false) {
        if (!topic) {
            return;
        }

        window.console.log(`Client subscribing to ${isChildTopic ? 'child ' : ''}topic: ${topic}`);
        this.client.subscribe(topic);
        topicCallbackMap[topic] = callback;
    },

    unsubscribeTopic(topic) {
        if (!topic) {
            return;
        }

        window.console.log(`Client unsubscribing to child topic: ${topic}`);
        try {
            this.client.unsubscribe(topic);
        } catch (error) {
            window.console.error(error);
        }
    },

    buildTopic(topic, postfix) {
        let completeTopic = topic;
        if (postfix) {
            completeTopic = `${completeTopic}_${postfix}`;
        }

        return `${globalsService.getDeployEnvironment()}_${completeTopic}`;
    },

    subscribeTopicList(isReconnect) {
        const {
            catQuestionSet,
            checkPlayerPauseStatus,
            extraTime,
            idVerificationUpdate,
            iepFileUpload,
            messages,
            remoteOralExamSessionUpdate,
            rewindAudio,
            settingsPersonal,
            startTest,
            waitingRoom,
        } = sessionReader.getPushChannels(true);

        try {
            this.subscribeTopic(catQuestionSet, handleCatQsReady.bind(this));
            this.subscribeTopic(checkPlayerPauseStatus, pauseStateHandler.handle.bind(pauseStateHandler));
            this.subscribeTopic(extraTime, handleExtraTime.bind(this));
            this.subscribeTopic(iepFileUpload, handleIEPFileUpload.bind(this));
            this.subscribeTopic(messages, handleMessage.bind(this));
            this.subscribeTopic(rewindAudio, handleRewindAudio.bind(this));
            this.subscribeTopic(settingsPersonal, updateSetting.bind(this));
            this.subscribeTopic(startTest, handleStartTest.bind(this));

            if (sessionReader.isActiveChildTestSession()) {
                this.subscribeChildTopics();
            }
            if (sessionReader.supportsLivestream()) {
                this.subscribeLiveSessionTopics(isReconnect);
            }
            if (remoteOralExamSessionUpdate) {
                this.subscribeTopic(remoteOralExamSessionUpdate, handleRemoteOralExamSessionUpdate.bind(this));
            }
            if (waitingRoom) {
                this.subscribeTopic(waitingRoom, handleWaitingRoomStatusUpdate.bind(this));
            }
            if (idVerificationUpdate) {
                this.subscribeTopic(idVerificationUpdate, handleIdVerificationUpdate.bind(this));
            }
        } catch (e) {
            console.warn('Failed connecting to channels in subscribeTopicList, will reconnect and retry', e);
            this.reconnectClient();
        }
    },

    onIOTConnectSuccess(isReconnect) {
        window.console.log('Client connected to AWS IoT, listening to topics');
        window.addEventListener('offline', onCandidateOffline);
        window.addEventListener('online', onCandidateBackOnline);
        const parsedUrl = queryString.parse(location.search);
        const { contentItemId, assessmentRunId } = parsedUrl;
        const objectIdToSubscribe = assessmentRunId || contentItemId;
        const pushChannels = sessionReader.getPushChannels(true);
        if (!sessionReader.isContextExam()) {
            const settingsTopic = this.buildTopic('player-common', objectIdToSubscribe);
            this.subscribeTopic(settingsTopic, updateSetting);
        } else if (typeof pushChannels === 'object') {
            this.subscribeTopicList(isReconnect);
        }
    },

    onIOTConnectFailure(e) {
        window.console.log('Client failed to connect to AWS IoT', e);
        this.reconnectClient();
    },

    setupClient(isReconnect = false) {
        const signedWebSocketUrl = sessionReader.getSignedWebSocketUrl(true);
        if (!signedWebSocketUrl) {
            window.console.warn('No web socket url available!');
            return;
        }
        // Disconnecting previous connected client, if any, before instantiating a new one.
        if (this.client && this.client.isConnected()) {
            try {
                this.client.onConnectionLost = () => {};
                this.client.disconnect();
            } catch (e) {
                window.console.warn('Unable to close existing client');
            }
        }
        this.client = new Paho.MQTT.Client(signedWebSocketUrl, uuid.v4());

        const connectOptions = {
            useSSL: true,
            timeout: 3,
            mqttVersion: 4,
            onSuccess: this.onIOTConnectSuccess.bind(this, isReconnect),
            onFailure: this.onIOTConnectFailure.bind(this),
        };

        this.client.onMessageArrived = this.onMessageArrived;
        this.client.onConnectionLost = this.onConnectionLost.bind(this);
        this.client.connect(connectOptions);
        window.iotDisconnectDebug = () => {
            this.client.disconnect();
        };
    },

    subscribeChildTopics() {
        const {
            catQuestionSet,
            checkPlayerPauseStatus,
            extraTime,
            iepFileUpload,
            rewindAudio,
            settingsPersonal,
            startTest,
        } = sessionReader.getPushChannels();

        try {
            this.subscribeTopic(catQuestionSet, handleCatQsReady.bind(this), true);
            this.subscribeTopic(checkPlayerPauseStatus, pauseStateHandler.handle.bind(pauseStateHandler), true);
            this.subscribeTopic(extraTime, handleExtraTime.bind(this), true);
            this.subscribeTopic(iepFileUpload, handleIEPFileUpload.bind(this), true);
            this.subscribeTopic(rewindAudio, handleRewindAudio.bind(this), true);
            this.subscribeTopic(settingsPersonal, updateSetting.bind(this), true);
            this.subscribeTopic(startTest, handleStartTest.bind(this), true);
        } catch (e) {
            console.warn('Failed connecting to channels in subscribeChildTopics, will reconnect and retry', e);
            // reconnectClient will reconnect if no other reconnects are in progress and will subscribe to child topics if in child test context
            this.reconnectClient();
        }
    },

    subscribeLiveSessionTopics(isReconnect) {
        const {
            liveSession,
        } = sessionReader.getPushChannels(true);

        try {
            const handleLivesessionMessage = liveSessionMessageHandler.handle.bind(liveSessionMessageHandler);
            this.subscribeTopic(liveSession, handleLivesessionMessage, true);
            if (isReconnect && sessionReader.getLiveSessionId() > 0) {
                const assessmentRunId = sessionReader.getAssessmentRunId(true);

                apiService.sendLiveSessionBackOnlineNotice(assessmentRunId);
            }
        } catch (e) {
            console.warn('Failed connecting to channels in subscribeLiveSessionTopics, will reconnect and retry', e);
            // reconnectClient will reconnect if no other reconnects are in progress and will subscribe to liveSession if in right context
            this.reconnectClient();
        }
    },

    unsubscribeChildTopics() {
        const {
            catQuestionSet,
            checkPlayerPauseStatus,
            extraTime,
            rewindAudio,
            settingsPersonal,
            startTest,
        } = sessionReader.getPushChannels();

        this.unsubscribeTopic(catQuestionSet);
        this.unsubscribeTopic(checkPlayerPauseStatus);
        this.unsubscribeTopic(extraTime);
        this.unsubscribeTopic(rewindAudio);
        this.unsubscribeTopic(settingsPersonal);
        this.unsubscribeTopic(startTest);
    },

    isClientConnected() {
        return this.client.isConnected();
    },
};

export default AWSIOTService;
